import type { ModelDefinition } from "@/models.js";

export const tencentModels = [
	{
		id: "hy3",
		name: "Hy3",
		description:
			"Tencent's 295B Mixture-of-Experts model with 21B active parameters, native 256K context support, and three reasoning modes for complex reasoning and agentic tasks.",
		family: "tencent",
		releasedAt: new Date("2026-07-06"),
		providers: [
			{
				providerId: "deepinfra",
				externalId: "tencent/Hy3",
				// DeepInfra's deployment is currently unreliable
				stability: "unstable",
				// DeepInfra retires this model on 2026-10-08 and silently
				// redirects requests to tencent/Hy4-preview afterwards.
				deactivatedAt: new Date("2026-10-08"),
				inputPrice: "0.14e-6",
				cachedInputPrice: "0.035e-6",
				outputPrice: "0.58e-6",
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 131072,
				quantization: "fp8",
				streaming: true,
				reasoning: true,
				reasoningEfforts: ["none", "low", "high"],
				vision: false,
				tools: true,
				jsonOutput: true,
				jsonOutputSchema: true,
			},
			{
				providerId: "novita",
				externalId: "tencent/hy3",
				inputPrice: "0.14e-6",
				cachedInputPrice: "0.035e-6",
				outputPrice: "0.58e-6",
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 262144,
				streaming: true,
				reasoning: true,
				reasoningEfforts: ["none", "low", "high"],
				vision: false,
				tools: true,
				// novita 400s on a named function choice for this deployment
				supportedToolChoices: ["auto", "none", "required"],
				jsonOutputSchema: true,
			},
			{
				providerId: "tencent",
				externalId: "hy3",
				inputPrice: "0.132e-6",
				cachedInputPrice: "0.033e-6",
				outputPrice: "0.528e-6",
				requestPrice: "0",
				contextSize: 262144,
				maxOutput: 131072,
				streaming: true,
				reasoning: true,
				// `max` is the one tier this deployment 400s on; the rest are
				// accepted and `none` genuinely reports zero reasoning tokens.
				reasoningEfforts: ["none", "minimal", "low", "medium", "high", "xhigh"],
				// Accepts image parts with a 200 but cannot read them — asked to
				// name the digit drawn in a test image it answered "8" for a "7",
				// while the sighted mappings all read it correctly.
				vision: false,
				tools: true,
				jsonOutput: true,
				jsonOutputSchema: true,
			},
		],
	},
	{
		id: "hy-mt2-plus",
		name: "Hy-MT2 Plus",
		description:
			"Tencent's Hunyuan translation model, tuned for translation quality and instruction following across a short 8K context.",
		family: "tencent",
		releasedAt: new Date("2026-05-21"),
		providers: [
			{
				providerId: "tencent",
				externalId: "hy-mt2-plus",
				inputPrice: "0.074e-6",
				outputPrice: "0.295e-6",
				requestPrice: "0",
				contextSize: 8192,
				maxOutput: 4096,
				streaming: true,
				vision: false,
				// A translation model: it answers the prompt but ignores tools,
				// `tool_choice` and `response_format` entirely rather than
				// rejecting them, so nothing beyond plain text is declared.
				tools: false,
				jsonOutput: false,
			},
		],
	},
	{
		id: "hy4-preview",
		name: "Hy4 Preview",
		description:
			"Tencent's preview release of Hy4, its next-generation Hunyuan reasoning model, with a 1M context window and preserved thinking.",
		family: "tencent",
		releasedAt: new Date("2026-08-28"),
		providers: [
			{
				providerId: "tencent",
				externalId: "hy4-preview",
				inputPrice: "0.834e-6",
				cachedInputPrice: "0.042e-6",
				outputPrice: "2.501e-6",
				requestPrice: "0",
				contextSize: 1000000,
				maxOutput: 65536,
				// The deployment accepts temperatures up to 2 without erroring but
				// then never returns: a one-line prompt answers in 7s at 1.0 and
				// still had produced nothing after 420s at 1.9. Clamp instead, or
				// high-temperature requests hang until the caller times out.
				maxTemperature: 1,
				streaming: true,
				reasoning: true,
				reasoningEfforts: [
					"none",
					"minimal",
					"low",
					"medium",
					"high",
					"xhigh",
					"max",
				],
				vision: false,
				tools: true,
				jsonOutput: true,
				jsonOutputSchema: true,
			},
		],
	},
	{
		id: "hy-image-v3.5-preview",
		name: "Hy Image 3.5 Preview",
		description:
			"Tencent's unified image generation and editing model, built on the Hy Image 3.0 MoE base, with up to 20 reference images, output up to 4K, and strong Chinese and English text rendering.",
		family: "tencent",
		output: ["text", "image"],
		releasedAt: new Date("2026-10-05"),
		providers: [
			{
				test: "skip",
				providerId: "tencent",
				externalId: "hy-image-v3.5-preview",
				inputPrice: "0",
				outputPrice: "0",
				imageOutputPrice: "1.6e-6",
				imageOutputTokensByResolution: {
					default: 20000,
				},
				contextSize: 100000,
				maxOutput: 4096,
				streaming: false,
				vision: true,
				tools: false,
				jsonOutput: false,
				imageGenerations: true,
			},
		],
	},
] as const satisfies ModelDefinition[];
